import type React from "react";
import { useState, useEffect, useRef } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { motion } from "framer-motion";
import CommentCardItem from "./CommentCardItem";
import CommentInput from "./CommentInput";
import type { ICommentType } from "../tabs/MemoriesTab";
import comments from "../../../../../../assets/comments.svg";

export interface CommentCardViewProps {
  messages: ICommentType[];
  isAdmin: boolean;
  socketRef: React.RefObject<WebSocket | null>;
  isMobile: boolean;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
  hasNextPage?: boolean;
  onNextPage?: () => void;
  onMessageSent?: () => void;
}

export default function CommentCardView({
  messages,
  isAdmin,
  socketRef,
  isMobile,
  handleApprove,
  handleDelete,
  hasNextPage,
  onNextPage,
  onMessageSent,
}: CommentCardViewProps) {
  const [allMessages, setAllMessages] = useState<ICommentType[]>(messages);
  const [loadingMore, setLoadingMore] = useState(false);
  // Use a ref so the effect can reliably read the "loading more" intent
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (loadingMoreRef.current) {
      // Append incoming messages, deduplicating by id
      setAllMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const incoming = messages.filter((m) => !existingIds.has(m.id));
        return [...prev, ...incoming];
      });
      loadingMoreRef.current = false;
      setLoadingMore(false);
    } else {
      // Initial load or a refresh (e.g. after a new message via WebSocket)
      setAllMessages(messages);
    }
  }, [messages]);

  const handleLoadMore = () => {
    loadingMoreRef.current = true;
    setLoadingMore(true);
    onNextPage?.();
  };

  const handleApproveLocal = (id: string, currentApproved: boolean) => {
    setAllMessages((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, is_approved: !currentApproved } : m,
      ),
    );
    handleApprove(id, currentApproved);
  };

  const handleDeleteLocal = (id: string) => {
    setAllMessages((prev) => prev.filter((m) => m.id !== id));
    handleDelete(id);
  };

  const visibleMessages = isAdmin
    ? allMessages
    : allMessages.filter((m) => m.is_approved);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Box sx={{ pb: 10 }}>
        {visibleMessages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 2,
            }}
          >
            <Box
              component="img"
              src={comments}
              alt="No messages yet"
              sx={{ width: 180, opacity: 0.6 }}
            />
            <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
              No memories shared yet. Be the first!
            </Typography>
          </Box>
        ) : (
          <>
            {/* Card grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 2,
              }}
            >
              {visibleMessages.map((comment) => (
                <CommentCardItem
                  key={comment.id}
                  comment={comment}
                  isAdmin={isAdmin}
                  handleApprove={handleApproveLocal}
                  handleDelete={handleDeleteLocal}
                />
              ))}
            </Box>

            {/* Load more */}
            {hasNextPage && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  startIcon={
                    loadingMore ? <CircularProgress size={16} /> : undefined
                  }
                  sx={{
                    borderRadius: "30px",
                    textTransform: "none",
                    px: 4,
                    fontWeight: 600,
                  }}
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Always-floating comment input */}
      <CommentInput
        socketRef={socketRef}
        isMobile={isMobile}
        serviceType="funeral"
        onMessageSent={onMessageSent}
      />
    </motion.div>
  );
}
